import { Sparkles } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Project, UUID } from '@/types/api'

import {
  AI_DEFAULT_COUNT,
  AI_EXAMPLE_PROMPTS,
  AI_MAX_PROMPT,
  AI_MIN_PROMPT,
  aiErrorMessage,
  isConnectionError,
  toDrafts,
  useAiConnection,
  useCommitDrafts,
  useCountOptions,
  useGenerateIssues,
  type DraftIssue,
} from './ai'
import { Button, Field, Input, Select, Textarea } from './components/Field'
import { Spinner } from './components/Feedback'
import { TypeIcon } from './components/IssueBadges'
import { Modal } from './components/Modal'
import { ISSUE_TYPES, PRIORITY_LABELS, TASK_PRIORITIES, TYPE_LABELS } from './filters'
import { cn, pluralize } from './lib/ui'
import type { IssueType, TaskPriority } from './types'
import { projectRef } from './useWorkspace'
import { useVoiceInput } from './useVoiceInput'
import { VoiceFieldControl } from './VoiceFieldControl'

export interface AiComposeDefaults {
  projectKey?: string | null
  sprintId?: UUID | null
}

type Step = 'prompt' | 'review'

/**
 * Draft several issues from one sentence, then review them before anything is
 * written.
 *
 * The two-step shape is the whole point. Generating straight into the board
 * would mean a person's first interaction with the feature is deleting four
 * issues they did not want; here the model's output is a proposal in a form
 * that is faster to correct than to retype, and nothing reaches the database
 * until "Add" is pressed.
 */
export function AiComposeModal({
  open,
  onClose,
  slug,
  projects,
  defaults,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  slug: string | null
  projects: Project[]
  defaults?: AiComposeDefaults
  onCreated?: (count: number) => void
}) {
  const [step, setStep] = useState<Step>('prompt')
  const [projectKey, setProjectKey] = useState('')
  const [prompt, setPrompt] = useState('')
  const [count, setCount] = useState(AI_DEFAULT_COUNT)
  const [drafts, setDrafts] = useState<DraftIssue[]>([])
  const [elapsed, setElapsed] = useState(0)

  const countOptions = useCountOptions()
  const generate = useGenerateIssues(slug, projectKey || null)
  const commit = useCommitDrafts(slug, projectKey || null)
  const voice = useVoiceInput(open && !generate.isPending && !commit.isPending)

  // useMutation hands back a new object every render, so the reset functions
  // are reached through refs: depending on the mutations themselves would wipe
  // the form the moment a submit turned them pending.
  const resetGenerate = useRef(generate.reset)
  resetGenerate.current = generate.reset
  const resetCommit = useRef(commit.reset)
  resetCommit.current = commit.reset

  const fallbackKey = useMemo(() => {
    if (defaults?.projectKey) return defaults.projectKey
    const preferred = projects.find((project) => project.is_default) ?? projects[0]
    return preferred ? projectRef(preferred) : ''
  }, [defaults?.projectKey, projects])

  // Start clean every time the dialog opens: a half-reviewed batch from last
  // time reappearing over a new brief would be genuinely alarming.
  useEffect(() => {
    if (!open) return
    setStep('prompt')
    setProjectKey(fallbackKey)
    setPrompt('')
    setCount(AI_DEFAULT_COUNT)
    setDrafts([])
    setElapsed(0)
    resetGenerate.current()
    resetCommit.current()
  }, [open, fallbackKey])

  // A generation takes several seconds. A counter is the difference between
  // "it is working" and "it has hung".
  useEffect(() => {
    if (!generate.isPending) return
    setElapsed(0)
    const started = Date.now()
    const timer = window.setInterval(() => {
      setElapsed(Math.round((Date.now() - started) / 1000))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [generate.isPending])

  const trimmed = prompt.trim()
  const canGenerate =
    Boolean(slug) &&
    Boolean(projectKey) &&
    trimmed.length >= AI_MIN_PROMPT &&
    trimmed.length <= AI_MAX_PROMPT &&
    !generate.isPending &&
    !voice.busy

  const runGenerate = () => {
    if (!canGenerate) return
    generate.mutate(
      {
        prompt: trimmed,
        count,
        ...(defaults?.sprintId ? { sprint_id: defaults.sprintId } : {}),
      },
      {
        onSuccess: (response) => {
          setDrafts(toDrafts(response.issues))
          setStep('review')
        },
      },
    )
  }

  const selected = drafts.filter((draft) => draft.selected)
  const canCommit = selected.length > 0 && !voice.busy &&
    selected.every((draft) => draft.title.trim().length > 0 && draft.title.trim().length <= 255)

  const patchDraft = (key: string, patch: Partial<DraftIssue>) => {
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)),
    )
  }

  const runCommit = () => {
    if (!canCommit || commit.isPending) return
    commit.mutate(
      { drafts: selected, sprintId: defaults?.sprintId ?? null },
      {
        onSuccess: (outcome) => {
          onCreated?.(outcome.created.length)
          if (outcome.failed.length === 0) {
            onClose()
            return
          }
          // Keep only what failed, so "Add" retries exactly those.
          const stillFailing = new Set(outcome.failed.map((entry) => entry.draft.key))
          setDrafts((current) =>
            current
              .filter((draft) => stillFailing.has(draft.key))
              .map((draft) => ({ ...draft, selected: true })),
          )
        },
      },
    )
  }

  // Escape and the backdrop always close. A generation in flight is aborted on
  // the way out rather than left running for an answer nobody will read; a
  // commit in flight is allowed to finish, because those issues are already
  // being written and the cache invalidation still runs.
  const handleClose = () => {
    voice.cancel()
    if (generate.isPending) generate.cancel()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Draft issues with AI"
      titleIcon={<Sparkles className="size-4 text-brand-600 dark:text-brand-400" aria-hidden="true" />}
      description={
        step === 'prompt'
          ? 'Describe the work. Nothing is created until you review and confirm.'
          : 'Edit anything, untick what you do not want, then add the rest.'
      }
      size="lg"
      footer={
        step === 'prompt' ? (
          <PromptFooter
            busy={generate.isPending}
            elapsed={elapsed}
            count={count}
            canGenerate={canGenerate}
            onCancel={generate.isPending ? generate.cancel : handleClose}
            onGenerate={runGenerate}
          />
        ) : (
          <ReviewFooter
            busy={commit.isPending}
            progress={commit.progress}
            selectedCount={selected.length}
            canAdd={canCommit}
            onBack={() => {
              voice.reset()
              resetCommit.current()
              setStep('prompt')
            }}
            onCancel={handleClose}
            onAdd={runCommit}
          />
        )
      }
    >
      {/* One live region for the whole dialog: a screen reader hears the
          generation start, finish and fail without hunting for the change. */}
      <p aria-live="polite" className="sr-only">
        {generate.isPending
          ? `Drafting issues. ${elapsed} seconds elapsed.`
          : commit.isPending
            ? `Adding issues. ${commit.progress.done} of ${commit.progress.total} done.`
            : step === 'review'
              ? `${drafts.length} issues drafted. ${selected.length} selected.`
              : ''}
      </p>

      {step === 'prompt' ? (
        <PromptStep
          projects={projects}
          projectKey={projectKey}
          onProjectKey={setProjectKey}
          prompt={prompt}
          onPrompt={setPrompt}
          count={count}
          onCount={setCount}
          countOptions={countOptions}
          pending={generate.isPending}
          voice={voice}
          elapsed={elapsed}
          error={generate.error}
        />
      ) : (
        <ReviewStep
          drafts={drafts}
          onPatch={patchDraft}
          onToggleAll={(next) =>
            setDrafts((current) => current.map((draft) => ({ ...draft, selected: next })))
          }
          failures={commit.data?.failed ?? []}
          error={commit.error}
          voice={voice}
          pending={commit.isPending}
        />
      )}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Step one: the brief                                                        */
/* -------------------------------------------------------------------------- */

function PromptStep({
  projects,
  projectKey,
  onProjectKey,
  prompt,
  onPrompt,
  count,
  onCount,
  countOptions,
  pending,
  voice,
  elapsed,
  error,
}: {
  projects: Project[]
  projectKey: string
  onProjectKey: (value: string) => void
  prompt: string
  onPrompt: (value: string) => void
  count: number
  onCount: (value: number) => void
  countOptions: number[]
  pending: boolean
  voice: ReturnType<typeof useVoiceInput>
  elapsed: number
  error: unknown
}) {
  const remaining = AI_MAX_PROMPT - prompt.trim().length

  return (
    <div className="space-y-3">
      {error ? <AiError error={error} /> : null}

      <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <Field label="Project" htmlFor="ai-project">
          <Select
            id="ai-project"
            value={projectKey}
            onChange={(event) => onProjectKey(event.target.value)}
            disabled={pending || projects.length <= 1}
          >
            {projects.length === 0 && <option value="">No projects</option>}
            {projects.map((project) => (
              <option key={project.id} value={projectRef(project)}>
                {project.name}
                {project.project_key ? ` (${project.project_key})` : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="How many issues"
          htmlFor="ai-count"
          hint="This decides the number, not the brief."
        >
          <Select
            id="ai-count"
            value={String(count)}
            onChange={(event) => onCount(Number(event.target.value))}
            disabled={pending}
          >
            {countOptions.map((value) => (
              <option key={value} value={String(value)}>
                {value} {value === 1 ? 'issue' : 'issues'}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        label="What needs doing?"
        htmlFor="ai-prompt"
        error={remaining < 0 ? `Keep the brief to ${AI_MAX_PROMPT} characters before generating.` : undefined}
        hint={
          remaining < 200
            ? `${remaining} characters left`
            : 'Say what outcome you want. Mention constraints, deadlines and anything already decided.'
        }
      >
        <Textarea
          id="ai-prompt"
          value={prompt}
          onChange={(event) => onPrompt(event.target.value)}
          maxLength={AI_MAX_PROMPT}
          rows={5}
          disabled={pending || voice.busy}
          dir="auto"
          placeholder="e.g. Harden the sign-in flow before the pilot: rate limiting, lockout after repeated failures, and an audit trail admins can read."
        />
        <VoiceFieldControl voice={voice} field="prompt" label="AI prompt" value={prompt}
          onChange={onPrompt} disabled={pending} />
      </Field>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-ink-600 dark:text-ink-400">Try one of these</p>
        <div className="flex flex-col gap-1.5">
          {AI_EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              type="button"
              disabled={pending || voice.busy}
              onClick={() => onPrompt(example)}
              className={cn(
                'rounded-md border border-ink-200 bg-ink-50 px-2.5 py-1.5 text-left text-xs text-ink-600 transition',
                'hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800 disabled:cursor-not-allowed disabled:opacity-60',
                'dark:border-ink-800 dark:bg-ink-950 dark:text-ink-300 dark:hover:border-brand-800 dark:hover:bg-brand-950',
              )}
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {pending ? (
        <div className="flex items-center gap-2 rounded-card border border-brand-200 bg-brand-50 px-3 py-2.5 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200">
          <Spinner className="text-brand-600 dark:text-brand-300" />
          <span>
            Drafting issues… {elapsed > 0 ? `${elapsed}s` : ''}
          </span>
          {/* The note has to stay true at the moment it is read. A flat
              "10–30 seconds" was a promise the request had already broken by
              the time anyone looked at it, which reads as a hang rather than as
              work in progress. After 30s the server may be on a second attempt,
              so say that instead of repeating a number the person can see is
              wrong. */}
          <span className="ml-auto text-xs opacity-80">
            {elapsed >= 30
              ? 'Still going — the first draft came back thin, so it is being redone.'
              : 'This usually takes 10–30 seconds.'}
          </span>
        </div>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Step two: review                                                           */
/* -------------------------------------------------------------------------- */

function ReviewStep({
  drafts,
  onPatch,
  onToggleAll,
  failures,
  error,
  voice,
  pending,
}: {
  drafts: DraftIssue[]
  onPatch: (key: string, patch: Partial<DraftIssue>) => void
  onToggleAll: (next: boolean) => void
  failures: Array<{ draft: DraftIssue; message: string }>
  error: unknown
  voice: ReturnType<typeof useVoiceInput>
  pending: boolean
}) {
  const allSelected = drafts.length > 0 && drafts.every((draft) => draft.selected)
  const failureByKey = new Map(failures.map((entry) => [entry.draft.key, entry.message]))

  return (
    <div className="space-y-3">
      {error ? <AiError error={error} /> : null}

      {failures.length > 0 ? (
        <div
          role="alert"
          className="rounded-card border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
        >
          {pluralize(failures.length, 'issue')} could not be added. Fix the highlighted rows and try
          again — the ones that worked are already on the board.
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-ink-600 dark:text-ink-400">
          <input
            type="checkbox"
            checked={allSelected}
            disabled={pending || voice.busy}
            onChange={(event) => onToggleAll(event.target.checked)}
            className="size-3.5 rounded border-ink-300 accent-brand-600"
          />
          Select all
        </label>
        <span className="ml-auto text-xs text-ink-500">
          {drafts.filter((draft) => draft.selected).length} of {drafts.length} selected
        </span>
      </div>

      <ul className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
        {drafts.map((draft, index) => (
          <DraftRow
            key={draft.key}
            index={index}
            draft={draft}
            failure={failureByKey.get(draft.key)}
            onPatch={(patch) => onPatch(draft.key, patch)}
            voice={voice}
            pending={pending}
          />
        ))}
      </ul>
    </div>
  )
}

function DraftRow({
  index,
  draft,
  failure,
  onPatch,
  voice,
  pending,
}: {
  index: number
  draft: DraftIssue
  failure?: string
  onPatch: (patch: Partial<DraftIssue>) => void
  voice: ReturnType<typeof useVoiceInput>
  pending: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const titleId = `ai-draft-title-${index}`

  return (
    <li
      className={cn(
        'rounded-card border px-3 py-2.5 transition',
        failure
          ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
          : draft.selected
            ? 'border-ink-200 bg-white dark:border-ink-800 dark:bg-ink-900'
            : 'border-ink-200 bg-ink-50 opacity-70 dark:border-ink-800 dark:bg-ink-950',
      )}
    >
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={draft.selected}
          disabled={pending || voice.busy}
          onChange={(event) => onPatch({ selected: event.target.checked })}
          aria-label={`Include "${draft.title}"`}
          className="mt-2 size-3.5 shrink-0 rounded border-ink-300 accent-brand-600"
        />

        <div className="min-w-0 flex-1 space-y-2">
          <label htmlFor={titleId} className="sr-only">
            Issue {index + 1} title
          </label>
          <Input
            id={titleId}
            value={draft.title}
            maxLength={255}
            disabled={pending || voice.busy}
            dir="auto"
            onChange={(event) => onPatch({ title: event.target.value })}
            className="font-medium"
          />
          <VoiceFieldControl voice={voice} field={`${draft.key}-title`} label={`Issue ${index + 1} title`}
            value={draft.title} onChange={(title) => onPatch({ title })} disabled={pending} />
          {(!draft.title.trim() || draft.title.trim().length > 255) && (
            <p className="text-xs text-red-700">Use a title between 1 and 255 characters.</p>
          )}

          {/* Each control is wrapped rather than given a width class: the
              shared CONTROL style is `w-full`, and Tailwind emits `.w-full`
              after the fixed widths, so a width on the element itself would
              silently lose. The wrapper sizes it instead. */}
          <div className="flex flex-wrap items-center gap-2">
            <TypeIcon type={draft.issue_type} />

            <div className="w-28">
              <Select
                aria-label={`Type for issue ${index + 1}`}
                value={draft.issue_type}
                onChange={(event) => onPatch({ issue_type: event.target.value as IssueType })}
                className="py-1 text-xs"
              >
                {ISSUE_TYPES.filter((value) => value !== 'subtask').map((value) => (
                  <option key={value} value={value}>
                    {TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
            </div>

            <div className="w-28">
              <Select
                aria-label={`Priority for issue ${index + 1}`}
                value={draft.priority}
                onChange={(event) => onPatch({ priority: event.target.value as TaskPriority })}
                className="py-1 text-xs"
              >
                {TASK_PRIORITIES.map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </div>

            <div className="w-16">
              <Input
                aria-label={`Story points for issue ${index + 1}`}
                value={draft.story_points === null ? '' : String(draft.story_points)}
                onChange={(event) => {
                  const raw = event.target.value.trim()
                  if (raw === '') {
                    onPatch({ story_points: null })
                    return
                  }
                  const points = Number(raw)
                  onPatch({ story_points: Number.isFinite(points) ? points : null })
                }}
                inputMode="decimal"
                placeholder="pts"
                className="py-1 text-xs"
              />
            </div>

            {draft.labels.length > 0 ? (
              <span className="max-w-40 truncate text-xs text-ink-500">
                {draft.labels.join(', ')}
              </span>
            ) : null}

            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              disabled={voice.busy || pending}
              className="ml-auto rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-950"
              aria-expanded={expanded}
            >
              {expanded ? 'Hide description' : 'Description'}
            </button>
          </div>

          {expanded ? (
            <>
              <label htmlFor={`ai-draft-desc-${index}`} className="sr-only">
                Issue {index + 1} description
              </label>
              <Textarea
                id={`ai-draft-desc-${index}`}
                value={draft.description}
                rows={3}
                disabled={pending || voice.busy}
                dir="auto"
                onChange={(event) => onPatch({ description: event.target.value })}
                className="text-xs"
              />
              <VoiceFieldControl voice={voice} field={`${draft.key}-description`} label={`Issue ${index + 1} description`}
                value={draft.description} onChange={(description) => onPatch({ description })} disabled={pending} />
            </>
          ) : draft.description ? (
            <p className="line-clamp-2 text-xs text-ink-500">{draft.description}</p>
          ) : null}

          {failure ? (
            <p className="text-xs text-red-700 dark:text-red-300">{failure}</p>
          ) : null}
        </div>
      </div>
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* Footers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The generate button names the COUNT it is about to produce.
 *
 * The count lives in a dropdown above, and it is the only thing that decides
 * how many issues come back — the brief is content, not instruction. So a brief
 * that opens "create 3 tasks" still produced five, because the dropdown said
 * five and nobody looks at a dropdown twice. Putting the number on the button
 * being pressed is what makes the two agree before the request is sent, rather
 * than after it comes back wrong.
 *
 * The brief deliberately does NOT override the dropdown: a real brief is full
 * of numbers ("rotation speed: 3, 5, 10 seconds") and none of them is a count.
 */
function PromptFooter({
  busy,
  elapsed,
  count,
  canGenerate,
  onCancel,
  onGenerate,
}: {
  busy: boolean
  elapsed: number
  count: number
  canGenerate: boolean
  onCancel: () => void
  onGenerate: () => void
}) {
  return (
    <>
      {busy ? (
        <span className="mr-auto text-xs text-ink-500">
          Working… {elapsed}s. You can cancel at any time.
        </span>
      ) : null}
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onGenerate} disabled={!canGenerate}>
        {busy ? 'Drafting…' : `Generate ${count} ${count === 1 ? 'issue' : 'issues'}`}
      </Button>
    </>
  )
}

function ReviewFooter({
  busy,
  progress,
  selectedCount,
  canAdd,
  onBack,
  onCancel,
  onAdd,
}: {
  busy: boolean
  progress: { done: number; total: number }
  selectedCount: number
  canAdd: boolean
  onBack: () => void
  onCancel: () => void
  onAdd: () => void
}) {
  return (
    <>
      {busy && progress.total > 0 ? (
        <span className="mr-auto text-xs text-ink-500">
          Adding {progress.done} of {progress.total}…
        </span>
      ) : null}
      <Button variant="ghost" onClick={onBack} disabled={busy}>
        Back
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onAdd} disabled={busy || !canAdd}>
        {busy
          ? 'Adding…'
          : selectedCount === 0
            ? 'Nothing selected'
            : `Add ${pluralize(selectedCount, 'issue')}`}
      </Button>
    </>
  )
}

/**
 * The AI failures do not share the generic banner: each one says its own thing.
 *
 * A connection failure gets one thing more. The workspace's Claude sign-in can
 * expire between opening this dialog and pressing Generate, and the only useful
 * response is to go and reconnect — so an admin is handed the link rather than
 * a sentence about a settings screen they then have to find.
 */
function AiError({ error }: { error: unknown }) {
  const ai = useAiConnection()
  const reconnect = isConnectionError(error) && ai.canManage && ai.settingsPath

  return (
    <div
      role="alert"
      className="rounded-card border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200"
    >
      {aiErrorMessage(error)}
      {reconnect ? (
        <>
          {' '}
          <Link
            to={ai.settingsPath as string}
            className="font-medium underline underline-offset-2"
          >
            Connect AI
          </Link>
        </>
      ) : null}
    </div>
  )
}
